import { useState, useEffect } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { todayWITA } from "@/lib/constants";
import { Stethoscope, MagnifyingGlass, Database, WarningCircle, CheckCircle, XCircle } from "@phosphor-icons/react";

const card = "rounded-2xl border border-slate-200 bg-white p-5 shadow-sm";
const inp = "w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-[#7C4DE0]";
const label = "mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500";

const StatusBadge = ({ status }) => {
  const map = {
    available: "bg-emerald-100 text-emerald-700", booked: "bg-indigo-100 text-indigo-700",
    blocked: "bg-rose-100 text-rose-700", buffer: "bg-amber-100 text-amber-700",
    selesai: "bg-slate-200 text-slate-600", batal: "bg-slate-100 text-slate-500",
  };
  return <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${map[status] || "bg-slate-100 text-slate-600"}`}>{status}</span>;
};

export default function Diagnostics() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [build, setBuild] = useState(null);
  const [staff, setStaff] = useState([]);
  const [services, setServices] = useState([]);
  const [form, setForm] = useState({ petugas: "", date: todayWITA(), jam: "09:00", service_id: "", lokasi: "Gerai" });
  const [slot, setSlot] = useState(null);
  const [slotBusy, setSlotBusy] = useState(false);
  const [backfill, setBackfill] = useState(null);
  const [bfBusy, setBfBusy] = useState(false);
  const [aliasName, setAliasName] = useState("Bidan Sri Rahayu,Amd.Keb(Ayu)");
  const [aliasRes, setAliasRes] = useState(null);
  const [resId, setResId] = useState("6a7c7fa6aa56725541e10875");
  const [resInsp, setResInsp] = useState(null);
  const [blkId, setBlkId] = useState("6a7e5adfa67036755d25d0ad");
  const [blkInsp, setBlkInsp] = useState(null);
  const [cand, setCand] = useState(null);
  const [cleanBusy, setCleanBusy] = useState(false);
  const [dryRun, setDryRun] = useState(null);

  useEffect(() => {
    if (!isSuper) return;
    api.get("/diagnostics/build").then((r) => setBuild(r.data)).catch(() => {});
    api.get("/staff").then((r) => setStaff(r.data || [])).catch(() => {});
    api.get("/services").then((r) => setServices(r.data || [])).catch(() => {});
  }, [isSuper]);

  const runSlot = async () => {
    if (!form.petugas || !form.date || !form.jam) return toast.error("Petugas, tanggal, jam wajib diisi");
    setSlotBusy(true); setSlot(null);
    try {
      const p = new URLSearchParams({ petugas: form.petugas, date: form.date, jam: form.jam });
      if (form.service_id) p.set("service_id", form.service_id);
      if (form.lokasi) p.set("lokasi", form.lokasi);
      const { data } = await api.get(`/diagnostics/slot?${p.toString()}`);
      setSlot(data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSlotBusy(false); }
  };

  const runBackfill = async () => {
    setBfBusy(true); setBackfill(null);
    try {
      const { data } = await api.get("/admin/staff-id-backfill");
      setBackfill(data);
      toast.success("Dry-run selesai (read-only)");
    } catch (e) { toast.error(apiError(e)); }
    finally { setBfBusy(false); }
  };

  const runAlias = async () => {
    setAliasRes(null);
    try { const { data } = await api.get(`/admin/backfill-preview?name=${encodeURIComponent(aliasName)}`); setAliasRes(data); }
    catch (e) { toast.error(apiError(e)); }
  };
  const runInspect = async () => {
    setResInsp(null);
    try { const { data } = await api.get(`/admin/reservation-inspect/${resId.trim()}`); setResInsp(data); }
    catch (e) { toast.error(apiError(e)); }
  };
  const runBlock = async () => {
    setBlkInsp(null);
    try { const { data } = await api.get(`/admin/block-inspect/${blkId.trim()}`); setBlkInsp(data); }
    catch (e) { toast.error(apiError(e)); }
  };
  const loadCand = async () => {
    setCand(null);
    try { const { data } = await api.get("/admin/block-cleanup-candidates"); setCand(data); }
    catch (e) { toast.error(apiError(e)); }
  };
  const runDryRun = async (mode) => {
    if (cleanBusy) return;
    setCleanBusy(true); setDryRun(null);
    try {
      const data = (await api.post(`/admin/block-cleanup?mode=${mode}&confirm=false`)).data;
      setDryRun({ mode, ...data });
      toast.success(`Dry-run selesai — ${data.would_delete || 0} block akan dihapus (belum diterapkan).`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setCleanBusy(false); }
  };
  const applyCleanup = async () => {
    if (cleanBusy || !dryRun) return;
    const mode = dryRun.mode;
    const n = dryRun.would_delete || 0;
    if (n === 0) { toast.info("Tidak ada yang perlu dihapus."); return; }
    if (!window.confirm(`Terapkan penghapusan ${n} block (${mode === "past" ? "lampau/historical" : "duplikat persis"})? Ini AMAN — tidak mengubah ketersediaan slot masa depan yang valid.`)) return;
    setCleanBusy(true);
    try {
      const res = (await api.post(`/admin/block-cleanup?mode=${mode}&confirm=true`)).data;
      toast.success(`Terhapus ${res.deleted} block (${mode}).`);
      setDryRun(null);
      loadCand();
    } catch (e) { toast.error(apiError(e)); }
    finally { setCleanBusy(false); }
  };

  if (!isSuper) {
    return <div className={card} data-testid="diag-denied"><p className="text-sm text-slate-600">Halaman ini khusus Super Admin.</p></div>;
  }

  return (
    <div className="space-y-6" data-testid="diagnostics-page">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#EEE6FF] text-[#6A38C7]"><Stethoscope size={24} weight="duotone" /></div>
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Diagnostik Jadwal</h1>
          <p className="text-sm text-slate-500">Alat read-only Super Admin — tidak menulis / menghapus data.</p>
        </div>
      </div>

      {/* Build marker */}
      <div className={card} data-testid="diag-build">
        <h2 className="mb-2 text-base font-bold text-slate-700">Versi Build Aktif</h2>
        {build ? (
          <div className="space-y-1 text-sm">
            <p><span className="text-slate-500">build:</span> <span className="font-mono font-bold text-[#6A38C7]" data-testid="diag-build-marker">{build.build}</span></p>
            <p className="text-slate-500">server: {build.server_time_wita}</p>
            <div className="flex flex-wrap gap-1.5 pt-1">
              {(build.features || []).map((f) => <span key={f} className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">{f}</span>)}
            </div>
          </div>
        ) : <p className="text-sm text-slate-400">Memuat…</p>}
      </div>

      {/* Slot inspector */}
      <div className={card} data-testid="diag-slot">
        <h2 className="mb-3 flex items-center gap-2 text-base font-bold text-slate-700"><MagnifyingGlass size={18} weight="bold" /> Inspeksi Slot (Kalender vs Booking Engine)</h2>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
          <div className="col-span-2 md:col-span-1">
            <label className={label}>Petugas</label>
            <select data-testid="diag-petugas" className={inp} value={form.petugas} onChange={(e) => setForm({ ...form, petugas: e.target.value })}>
              <option value="">Pilih…</option>
              {staff.map((s) => <option key={s.id} value={s.nama}>{s.nama}{s.aktif === false ? " (nonaktif)" : ""}</option>)}
            </select>
          </div>
          <div><label className={label}>Tanggal</label><input data-testid="diag-date" type="date" className={inp} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
          <div><label className={label}>Jam</label><input data-testid="diag-jam" type="time" className={inp} value={form.jam} onChange={(e) => setForm({ ...form, jam: e.target.value })} /></div>
          <div>
            <label className={label}>Layanan (opsional)</label>
            <select data-testid="diag-service" className={inp} value={form.service_id} onChange={(e) => setForm({ ...form, service_id: e.target.value })}>
              <option value="">(default interval)</option>
              {services.map((s) => <option key={s.id || s._id} value={s.id || s._id}>{s.nama} ({s.durasi || "?"}m)</option>)}
            </select>
          </div>
          <div>
            <label className={label}>Lokasi</label>
            <select data-testid="diag-lokasi" className={inp} value={form.lokasi} onChange={(e) => setForm({ ...form, lokasi: e.target.value })}>
              <option value="Gerai">Gerai</option><option value="Homecare">Homecare</option>
            </select>
          </div>
        </div>
        <button data-testid="diag-run-slot" onClick={runSlot} disabled={slotBusy} className="mt-3 rounded-xl bg-[#7C4DE0] px-5 py-2.5 text-sm font-bold text-white hover:bg-[#6A38C7] disabled:opacity-50">
          {slotBusy ? "Mendiagnosa…" : "Diagnosa Slot"}
        </button>

        {slot && (
          <div className="mt-4 space-y-3" data-testid="diag-slot-result">
            {slot.DIVERGENCE && (
              <div className="flex items-start gap-2 rounded-xl border border-rose-300 bg-rose-50 p-3" data-testid="diag-divergence">
                <WarningCircle size={20} weight="fill" className="mt-0.5 shrink-0 text-rose-600" />
                <div><p className="text-sm font-bold text-rose-700">DIVERGENCE terdeteksi</p><p className="text-xs text-rose-600">{slot.divergence_note}</p></div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              <div><p className={label}>Kalender (untuk layanan ini)</p>
                {slot.calendar_bookable
                  ? <span className="inline-flex items-center gap-1 text-sm font-bold text-emerald-600" data-testid="diag-cal-ok"><CheckCircle size={16} weight="fill" /> Bisa dibooking</span>
                  : <span className="inline-flex items-center gap-1 text-sm font-bold text-rose-600" data-testid="diag-cal-blocked"><XCircle size={16} weight="fill" /> Tak bisa</span>}
                <p className="mt-0.5 text-[11px] text-slate-400">status sel: <StatusBadge status={slot.calendar_status} /></p>
              </div>
              <div><p className={label}>Booking Engine</p>{slot.booking_engine.bookable
                ? <span className="inline-flex items-center gap-1 text-sm font-bold text-emerald-600" data-testid="diag-engine-ok"><CheckCircle size={16} weight="fill" /> Bisa dibooking</span>
                : <span className="inline-flex items-center gap-1 text-sm font-bold text-rose-600" data-testid="diag-engine-blocked"><XCircle size={16} weight="fill" /> Ditolak</span>}</div>
              <div><p className={label}>Alasan</p><p className="text-sm text-slate-700">{slot.booking_engine.reason || "-"}</p></div>
              <div><p className={label}>Interval Dicoba</p><p className="text-sm text-slate-700">{slot.attempted_interval.start} – {slot.attempted_interval.end}</p></div>
            </div>
            {slot.booking_engine.conflict_record && (
              <div className="rounded-xl bg-slate-50 p-3 text-xs" data-testid="diag-conflict-record">
                <p className="font-bold text-slate-600">Record penyebab konflik:</p>
                <p className="font-mono text-slate-700">kind={slot.booking_engine.conflict_record.kind} · id={slot.booking_engine.conflict_record.record_id}
                  {slot.booking_engine.conflict_record.alasan ? ` · alasan="${slot.booking_engine.conflict_record.alasan}"` : ""}
                  {slot.booking_engine.conflict_record.nama ? ` · pasien="${slot.booking_engine.conflict_record.nama}"` : ""}
                  {slot.booking_engine.conflict_record.source_reservation_id ? ` · source_res=${slot.booking_engine.conflict_record.source_reservation_id}` : ""}
                </p>
              </div>
            )}
            <div className="rounded-xl bg-slate-50 p-3 text-xs" data-testid="diag-staff-info">
              <p className="font-bold text-slate-600">Staff mapping:</p>
              <p className="text-slate-700">nama="{slot.staff.queried_name}" · staff_id={slot.staff.staff_id || "-"} · matched={String(slot.staff.matched_staff)} · interval={slot.staff.slot_interval}m · buffer={slot.staff.homecare_travel_buffer}m
                {slot.staff.mapping_mismatch ? <span className="ml-1 font-bold text-rose-600">MISMATCH (nama tidak cocok staff manapun)</span> : ""}</p>
            </div>
            <div data-testid="diag-occupied">
              <p className={label}>Interval Terpakai ({slot.occupied_intervals.length})</p>
              {slot.occupied_intervals.length === 0 ? <p className="text-sm text-slate-400">Kosong — tidak ada reservasi/block.</p> : (
                <div className="overflow-x-auto rounded-xl border border-slate-100">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 text-slate-500"><tr><th className="p-2 text-left">Kind</th><th className="p-2 text-left">Mulai–Selesai</th><th className="p-2 text-left">Record ID</th><th className="p-2 text-left">Detail</th></tr></thead>
                    <tbody>
                      {slot.occupied_intervals.map((o, i) => (
                        <tr key={i} className="border-t border-slate-100">
                          <td className="p-2 font-semibold">{o.kind}</td>
                          <td className="p-2">{o.start_hhmm}–{o.end_hhmm}</td>
                          <td className="p-2 font-mono">{o.record_id}</td>
                          <td className="p-2 text-slate-500">{o.alasan || o.nama || o.note || o.status || ""}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Backfill dry-run */}
      <div className={card} data-testid="diag-backfill">
        <h2 className="mb-2 flex items-center gap-2 text-base font-bold text-slate-700"><Database size={18} weight="bold" /> Audit staff_id (Dry-run, read-only)</h2>
        <p className="mb-3 text-xs text-slate-500">Menghitung reservasi/block tanpa staff_id kanonik + nama UNMATCHED. Tidak menulis apa pun.</p>
        <button data-testid="diag-run-backfill" onClick={runBackfill} disabled={bfBusy} className="rounded-xl bg-slate-800 px-5 py-2.5 text-sm font-bold text-white hover:bg-slate-900 disabled:opacity-50">
          {bfBusy ? "Menjalankan…" : "Jalankan Dry-run"}
        </button>
        {backfill && (
          <div className="mt-4 space-y-3" data-testid="diag-backfill-result">
            {["reservations", "blocked_slots"].map((coll) => {
              const r = backfill[coll] || {};
              return (
                <div key={coll} className="rounded-xl border border-slate-100 p-3">
                  <p className="text-sm font-bold text-slate-700">{coll}</p>
                  <p className="text-xs text-slate-600">tanpa staff_id: <b>{r.missing_staff_id_total}</b> · akan dipetakan: <b className="text-emerald-600">{r.will_map_total}</b> · UNMATCHED: <b className={r.unmatched_total ? "text-rose-600" : "text-slate-500"}>{r.unmatched_total}</b></p>
                  {(r.unmatched_names || []).length > 0 && (
                    <div className="mt-2 space-y-1" data-testid={`diag-unmatched-${coll}`}>
                      <p className="text-[11px] font-bold uppercase text-rose-600">Nama UNMATCHED (butuh pemetaan alias):</p>
                      {r.unmatched_names.map((u, i) => (
                        <p key={i} className="text-xs text-slate-700">• "<b>{u.petugas_nama}</b>" ×{u.count} {u.suggested_staff ? <span className="font-semibold text-emerald-700">→ {u.suggested_staff.canonical_nama} ({u.suggested_staff.staff_id})</span> : <span className="text-rose-500">(tak ada saran)</span>} <span className="font-mono text-slate-400">ids: {(u.sample_ids || []).join(", ")}</span></p>
                      ))}
                    </div>
                  )}
                  {(r.mapping_recognized || []).length > 0 && (
                    <details className="mt-2"><summary className="cursor-pointer text-[11px] font-semibold text-slate-500">Mapping dikenali ({r.mapping_recognized.length})</summary>
                      {r.mapping_recognized.map((m, i) => <p key={i} className="text-xs text-slate-600">• "{m.petugas_nama}" → {m.staff_id} ×{m.count}</p>)}
                    </details>
                  )}
                </div>
              );
            })}
            {backfill.blocked_slots_audit && (
              <div className="rounded-xl border border-slate-100 p-3" data-testid="diag-block-audit">
                <p className="text-sm font-bold text-slate-700">Audit blocked_slots</p>
                <p className="text-xs text-slate-600">total: <b>{backfill.blocked_slots_audit.total}</b> · upcoming/hari ini: <b className="text-indigo-600">{backfill.blocked_slots_audit.upcoming_or_today}</b> · past: <b className="text-slate-500">{backfill.blocked_slots_audit.past}</b> · duplikat (grup): <b className={backfill.blocked_slots_audit.duplicate_slot_groups ? "text-amber-600" : "text-slate-500"}>{backfill.blocked_slots_audit.duplicate_slot_groups}</b></p>
                <p className="mt-1 text-xs text-slate-600">manual vs generated: {Object.entries(backfill.blocked_slots_audit.manual_vs_generated || {}).map(([k, v]) => `${k}=${v}`).join(" · ")}</p>
                <p className="mt-1 text-[11px] text-slate-400">{backfill.blocked_slots_audit.note_overlap}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Alias mapping preview + reservation inspect (read-only) */}
      <div className={card} data-testid="diag-preview">
        <h2 className="mb-2 text-base font-bold text-slate-700">Preview Pemetaan Alias & Inspeksi Reservasi (read-only)</h2>
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className={label}>Nama alias (petugas_nama)</label>
            <div className="flex gap-2">
              <input data-testid="diag-alias-name" className={inp} value={aliasName} onChange={(e) => setAliasName(e.target.value)} />
              <button data-testid="diag-run-alias" onClick={runAlias} className="shrink-0 rounded-xl bg-[#7C4DE0] px-4 py-2 text-sm font-bold text-white hover:bg-[#6A38C7]">Preview</button>
            </div>
            {aliasRes && (
              <div className="mt-2 rounded-xl bg-slate-50 p-3 text-xs" data-testid="diag-alias-result">
                {aliasRes.canonical
                  ? <p className="text-slate-700">"{aliasRes.input_name}" → <b className="text-emerald-700">{aliasRes.canonical.nama}</b> <span className="font-mono">({aliasRes.canonical.staff_id})</span></p>
                  : <p className="text-rose-600">Tidak ada staff kanonik yang cocok.</p>}
                <p className="mt-1 text-slate-600">akan dipetakan → reservations: <b>{aliasRes.would_map.reservations}</b> · blocked_slots: <b>{aliasRes.would_map.blocked_slots}</b> · <span className="font-semibold text-amber-600">apply: {String(aliasRes.apply)}</span></p>
              </div>
            )}
          </div>
          <div>
            <label className={label}>Reservation ID</label>
            <div className="flex gap-2">
              <input data-testid="diag-res-id" className={inp} value={resId} onChange={(e) => setResId(e.target.value)} />
              <button data-testid="diag-run-inspect" onClick={runInspect} className="shrink-0 rounded-xl bg-slate-800 px-4 py-2 text-sm font-bold text-white hover:bg-slate-900">Inspeksi</button>
            </div>
            {resInsp && (
              <div className="mt-2 rounded-xl bg-slate-50 p-3 text-xs" data-testid="diag-res-result">
                <p className="font-bold text-slate-600">Record:</p>
                <p className="text-slate-700">pasien="{resInsp.record.nama_lengkap || "-"}" · petugas="{resInsp.record.petugas_nama || "(kosong)"}" · staff_id={resInsp.record.staff_id || "-"}</p>
                <p className="text-slate-700">{resInsp.record.tanggal_kunjungan} {resInsp.record.jam_kunjungan} · {resInsp.record.jenis_layanan} · {resInsp.record.lokasi} · status={resInsp.record.status}</p>
                <p className="text-slate-700">created_by={resInsp.record.created_by || "-"} · created_at={resInsp.record.created_at || "-"} · source={resInsp.record.source || "-"}</p>
                <p className="mt-1 font-bold text-slate-600">Kandidat petugas ({resInsp.candidates.length}):</p>
                {resInsp.candidates.length === 0 ? <p className="text-slate-500">Tidak ada kandidat otomatis — putuskan manual.</p>
                  : resInsp.candidates.map((c, i) => <p key={i} className="text-slate-700">• [{c.source}] {c.nama || "-"} {c.staff_id ? `(${c.staff_id})` : ""}</p>)}
                <p className="mt-1 text-[11px] text-rose-500">{resInsp.note}</p>
              </div>
            )}
          </div>
        </div>
        <div className="mt-4 border-t border-slate-100 pt-3">
          <label className={label}>Block ID (blocked_slots)</label>
          <div className="flex gap-2">
            <input data-testid="diag-block-id" className={inp} value={blkId} onChange={(e) => setBlkId(e.target.value)} />
            <button data-testid="diag-run-block" onClick={runBlock} className="shrink-0 rounded-xl bg-rose-600 px-4 py-2 text-sm font-bold text-white hover:bg-rose-700">Inspeksi Block</button>
          </div>
          {blkInsp && (
            <div className="mt-2 rounded-xl bg-slate-50 p-3 text-xs" data-testid="diag-block-result">
              <p className="text-slate-700">petugas="{blkInsp.block.petugas_nama}" · staff_id={blkInsp.block.staff_id || "-"} · tanggal={blkInsp.block.tanggal} · jam={blkInsp.block.jam}</p>
              <p className="text-slate-700">alasan="{blkInsp.block.alasan || "-"}" · created_by={blkInsp.block.created_by || "(kosong)"} · created_at={blkInsp.block.created_at || "-"}</p>
              <p className="mt-1 font-bold text-slate-600">Provenance: <span className="text-rose-700">{blkInsp.provenance_guess}</span></p>
              <p className="text-slate-600">blok sehari utk staff ini: <b>{blkInsp.same_day_blocks_for_staff}</b> · duplikat slot persis: <b>{blkInsp.same_slot_duplicates}</b></p>
              <p className="mt-1 text-[11px] text-slate-400">field keys: {(blkInsp.all_field_keys || []).join(", ")}</p>
              <p className="text-[11px] text-rose-500">{blkInsp.note}</p>
            </div>
          )}
        </div>
      </div>

      <div className={card} data-testid="diag-cleanup">
        <h2 className="mb-2 text-base font-bold text-slate-700">Kandidat & Cleanup Block (aman/idempoten)</h2>
        <div className="flex flex-wrap gap-2">
          <button data-testid="diag-load-candidates" onClick={loadCand} className="rounded-xl bg-[#7C4DE0] px-4 py-2 text-sm font-bold text-white hover:bg-[#6A38C7]">Lihat Kandidat</button>
          <button data-testid="diag-dryrun-dup" onClick={() => runDryRun("duplicates")} disabled={cleanBusy} className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-2 text-sm font-bold text-amber-700 hover:bg-amber-100 disabled:opacity-50">Dry-run Hapus Duplikat</button>
          <button data-testid="diag-dryrun-past" onClick={() => runDryRun("past")} disabled={cleanBusy} className="rounded-xl border border-slate-300 bg-slate-50 px-4 py-2 text-sm font-bold text-slate-600 hover:bg-slate-100 disabled:opacity-50">Dry-run Hapus Block Lampau</button>
        </div>
        <p className="mt-2 text-[11px] text-slate-400">Klik Dry-run untuk melihat rincian dulu. Tombol Apply hanya muncul setelah Anda review hasil dry-run. Tidak ada yang dihapus sebelum Anda tekan Apply.</p>

        {dryRun && (
          <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50/60 p-3" data-testid="diag-dryrun-result">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-bold text-amber-800">Hasil DRY-RUN — mode: {dryRun.mode === "past" ? "Block Lampau" : "Duplikat Persis"} <span className="ml-1 rounded-full bg-white px-2 py-0.5 text-[11px] font-bold text-amber-700">belum diterapkan</span></p>
              <button data-testid="diag-dryrun-close" onClick={() => setDryRun(null)} className="text-xs font-semibold text-slate-500 hover:text-slate-700">Tutup</button>
            </div>
            <div className="mt-2 grid grid-cols-2 gap-3 text-xs md:grid-cols-3">
              <div><p className={label}>Akan dihapus</p><p className="text-lg font-bold text-rose-600" data-testid="diag-would-delete">{dryRun.would_delete || 0}</p></div>
              {dryRun.mode === "duplicates" && <div><p className={label}>Duplicate groups</p><p className="text-lg font-bold text-amber-700" data-testid="diag-dup-groups">{dryRun.duplicate_groups || 0}</p></div>}
              <div><p className={label}>Sample IDs (dihapus)</p><p className="font-mono text-[11px] text-slate-600" data-testid="diag-sample-ids">{(dryRun.sample_ids || []).length ? (dryRun.sample_ids || []).join(", ") : "—"}</p></div>
            </div>
            {dryRun.note && <p className="mt-2 text-[11px] text-slate-500">{dryRun.note}</p>}

            {dryRun.mode === "duplicates" && (dryRun.sample_groups || []).length > 0 && (
              <div className="mt-3 overflow-x-auto rounded-xl border border-amber-100 bg-white" data-testid="diag-sample-groups">
                <table className="w-full text-[11px]">
                  <thead className="bg-amber-50 text-amber-700"><tr>
                    <th className="p-2 text-left">Grup (petugas · tanggal · jam)</th><th className="p-2 text-left">Total</th>
                    <th className="p-2 text-left">DIPERTAHANKAN (keep_id)</th><th className="p-2 text-left">DIHAPUS (delete_ids)</th>
                  </tr></thead>
                  <tbody>
                    {dryRun.sample_groups.map((g, i) => (
                      <tr key={i} className="border-t border-amber-50">
                        <td className="p-2 text-slate-700">{g.petugas_nama || "(kosong)"} · {g.tanggal} · {g.jam}</td>
                        <td className="p-2 font-bold text-slate-600">{g.total_in_group}</td>
                        <td className="p-2 font-mono text-emerald-700">{g.keep_id}</td>
                        <td className="p-2 font-mono text-rose-600">{(g.delete_ids || []).join(", ")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="p-2 text-[10px] text-slate-400">Menampilkan hingga 10 grup contoh. keep_id = record yang dipertahankan (satu per grup).</p>
              </div>
            )}

            <div className="mt-3 flex items-center gap-2">
              <button data-testid="diag-apply-cleanup" onClick={applyCleanup} disabled={cleanBusy || (dryRun.would_delete || 0) === 0}
                className="rounded-xl bg-rose-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-rose-700 disabled:opacity-50">
                {cleanBusy ? "Menerapkan…" : `Apply Hapus ${dryRun.mode === "past" ? "Block Lampau" : "Duplikat"} (${dryRun.would_delete || 0})`}
              </button>
              <button data-testid="diag-cancel-cleanup" onClick={() => setDryRun(null)} className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-bold text-slate-600 hover:bg-slate-50">Batal</button>
            </div>
          </div>
        )}

        {cand && (
          <div className="mt-3 space-y-2 text-xs" data-testid="diag-candidates-result">
            <p className="text-slate-600">total block: <b>{cand.total_blocks}</b></p>
            <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-2">
              <p className="font-bold text-emerald-700">Aman dibersihkan:</p>
              <p>past/historical: <b>{cand.safe_to_clean.past_historical.count}</b> · duplikat extras: <b>{cand.safe_to_clean.exact_duplicates_extras.count}</b> (grup {cand.safe_to_clean.exact_duplicates_extras.groups})</p>
            </div>
            <div className="rounded-xl border border-amber-100 bg-amber-50 p-2">
              <p className="font-bold text-amber-700">Perlu review (TIDAK auto-hapus):</p>
              <p>tanpa staff_id (mappable): <b>{cand.needs_review_NOT_auto_deleted.no_staff_id_mappable.count}</b> · tak mappable: <b>{cand.needs_review_NOT_auto_deleted.no_staff_id_unmappable.count}</b> · legacy tanpa created_by: <b>{cand.needs_review_NOT_auto_deleted.legacy_no_created_by.count}</b></p>
            </div>
            <p className="text-[11px] text-rose-500">{cand.warning}</p>
          </div>
        )}
      </div>
    </div>
  );
}
